/**
 * Low patrol-ship engine drone — level and stereo pan from nearest craft.
 */
export class ShipAudio {
  /**
   * @param {AudioContext} ctx
   * @param {GainNode} bus
   * @param {import('../config.js').CONFIG['audio']['ships']} config
   */
  constructor(ctx, bus, config) {
    this._ctx = ctx;
    this._bus = bus;
    this._config = config;
    this._mix = 0;
    this._started = false;

    /** @type {OscillatorNode[]} */
    this._oscillators = [];
    /** @type {GainNode | null} */
    this._engineGain = null;
    /** @type {StereoPannerNode | null} */
    this._panner = null;
    /** @type {BiquadFilterNode | null} */
    this._filter = null;
  }

  start() {
    if (this._started) return;
    this._started = true;

    this._panner = this._ctx.createStereoPanner();
    this._panner.pan.value = 0;

    this._filter = this._ctx.createBiquadFilter();
    this._filter.type = 'lowpass';
    this._filter.frequency.value = this._config.filterHz;
    this._filter.Q.value = 0.5;

    this._engineGain = this._ctx.createGain();
    this._engineGain.gain.value = 0;

    const freqs = this._config.engineFrequencies;
    for (let i = 0; i < freqs.length; i += 1) {
      const osc = this._ctx.createOscillator();
      osc.type = i === 0 ? 'sawtooth' : 'sine';
      osc.frequency.value = freqs[i];

      const partial = this._ctx.createGain();
      partial.gain.value = i === 0 ? 1 : 0.42;

      const lfo = this._ctx.createOscillator();
      lfo.frequency.value = 0.11 + i * 0.04;
      const lfoGain = this._ctx.createGain();
      lfoGain.gain.value = 3 + i * 2;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.detune);

      osc.connect(partial);
      partial.connect(this._filter);
      osc.start();
      lfo.start();
      this._oscillators.push(osc, lfo);
    }

    this._filter.connect(this._engineGain);
    this._engineGain.connect(this._panner);
    this._panner.connect(this._bus);
  }

  /**
   * @param {number} delta
   * @param {{
   *   inSimulation?: boolean;
   *   shipAudio?: {
   *     distance: number;
   *     pan: number;
   *     audibleCount: number;
   *   };
   * }} state
   */
  update(delta, state) {
    if (!this._started || !this._engineGain || !this._panner) return;

    const probe = state.inSimulation ? state.shipAudio : null;
    const distance = probe?.distance ?? Infinity;
    const max = this._config.maxHearDistance;
    let target = 0;
    if (probe && probe.audibleCount > 0 && distance < max) {
      const t = 1 - distance / max;
      target =
        t * t * this._config.engineGain * Math.min(1, 0.55 + probe.audibleCount * 0.12);
    }

    this._mix += (target - this._mix) * Math.min(1, delta * 4);
    this._engineGain.gain.value = this._mix;

    if (probe && Number.isFinite(probe.pan)) {
      const pan = Math.max(-1, Math.min(1, probe.pan * this._config.panStrength));
      this._panner.pan.value += (pan - this._panner.pan.value) * Math.min(1, delta * 8);
    }

    if (this._filter) {
      const lift = this._mix * this._config.filterSweep;
      this._filter.frequency.value =
        this._config.filterHz + lift;
    }
  }

  dispose() {
    for (const osc of this._oscillators) {
      try {
        osc.stop();
      } catch {
        /* noop */
      }
      osc.disconnect();
    }
    this._oscillators = [];
    this._filter?.disconnect();
    this._filter = null;
    this._engineGain?.disconnect();
    this._engineGain = null;
    this._panner?.disconnect();
    this._panner = null;
    this._started = false;
  }
}
